import React, { useState, useMemo } from "react";
import { Stack, Card, Text } from "@chakra-ui/react";
import axios from "../axios/axios";
import TaskTable from "../components/TaskTable";
import formatHoursAndMinutes from "../lib/helpers/formatHoursAndMinutes";
import { useTasks } from "../providers/TasksProvider";
import { Header } from "../components/Header";
import { format } from "date-fns";
import moment from "moment";

export default function AdminTaskContainer({ user }) {
  const { inProgressTasks, fetchTasks } = useTasks();
  const [selectedDate] = useState(new Date());

  const tasksForSelectedDate = useMemo(
    () =>
      inProgressTasks.filter(task => {
        if (!task.created_at && !task.date) {
          console.warn("Tâche sans date:", task);
          return false;
        }

        const taskDate = format(new Date(task.date || task.created_at), "yyyy-MM-dd");
        const selectedDateFormatted = format(selectedDate, "yyyy-MM-dd");

        return taskDate === selectedDateFormatted;
      }),
    [inProgressTasks, selectedDate]
  );

  const previousUserTasks = useMemo(
    () =>
      inProgressTasks.filter(task => {
        if (!task.created_at && !task.date) return false;
        const taskDate = format(new Date(task.date || task.created_at), "yyyy-MM-dd");
        const selectedDateFormatted = format(selectedDate, "yyyy-MM-dd");
        return taskDate !== selectedDateFormatted;
      }),
    [inProgressTasks, selectedDate]
  );

  const uniqueDates = [...new Set(previousUserTasks.map(task => moment(task.created_at).format("YYYY-MM-DD")))].sort((a, b) => moment(a).diff(moment(b)));

  const activeUserTasks = useMemo(() => tasksForSelectedDate.filter(task => task.completed === 0), [tasksForSelectedDate]);

  const handleDeleteTask = taskUniqueId => {
    axios
      .delete(`/user_task/${taskUniqueId}`)
      .then(() => {
        fetchTasks();
      })
      .catch(error => console.error("Erreur lors de la suppression de la tâche:", error));
  };

  const onEditTask = (taskId, timeSpent) => {
    axios
      .put(`/user_task/${taskId}`, {
        time_spent: timeSpent,
      })
      .then(() => {
        fetchTasks();
      })
      .catch(error => console.error("Erreur lors de la modification de la tâche:", error));
  };

  const totalCumulatedTime = useMemo(
    () =>
      tasksForSelectedDate.reduce((totalTime, task) => {
        const timeSpent = task.time_spent ? parseFloat(task.time_spent) : 0;
        return totalTime + timeSpent;
      }, 0) / 60,
    [tasksForSelectedDate]
  );

  const handleMarkAsCompleted = taskId => {
    axios
      .post("/mark_task_completed", {
        task_id: taskId,
      })
      .then(() => {
        fetchTasks();
      })
      .catch(error => console.error("Error marking task as completed:", error));
  };

  const onDuplicateTask = taskId => {
    axios
      .post("/duplicate_task", {
        task_id: taskId,
      })
      .then(() => {
        fetchTasks();
      })
      .catch(error => console.error("Error duplicating the task:", error));
  };

  return (
    <>
      {uniqueDates
        .filter(date => previousUserTasks.some(task => moment(task.created_at).isSame(date, "day") && !task.completed))
        .map(date => {
          const tasksForDate = previousUserTasks.filter(task => moment(task.created_at).isSame(date, "day"));
          const totalTimeForDate = tasksForDate.reduce((total, task) => total + parseFloat(task.time_spent), 0) / 60;

          return (
            <Card display={"flex"} justifyContent={"center"} alignItems={"center"} key={date}>
              <Stack direction={"row"} p={15}>
                <Header as={"h3"} size={"md"}>
                  Tâches du {moment(date).format("DD/MM/YYYY")} :
                </Header>
              </Stack>
              {totalTimeForDate < 7 && (
                <Text color="red.500" mb={2}>
                  ⚠️ Attention : Seulement {formatHoursAndMinutes(totalTimeForDate)} de comptabilisées sur ce jour
                </Text>
              )}
              <TaskTable
                userTasks={previousUserTasks.filter(task => moment(task.created_at).isSame(date, "day") && !task.completed)}
                onDuplicateTask={onDuplicateTask}
                onEditTask={onEditTask}
                onDeleteTask={handleDeleteTask}
                onMarkAsCompleted={handleMarkAsCompleted}
              />
            </Card>
          );
        })}
      <Card backgroundColor={"white"} display={"flex"} alignItems={"center"} width={{ base: "95%", md: "100%" }} mx="auto">
        <Stack direction={{ base: "column", md: "row" }} p={{ base: 3, md: 15 }} width="100%">
          <Header as={"h3"} size={"md"}>
            Tâches effectuées :
          </Header>
        </Stack>
        <TaskTable
          userTasks={activeUserTasks}
          onDeleteTask={handleDeleteTask}
          onMarkAsCompleted={handleMarkAsCompleted}
          onDuplicateTask={onDuplicateTask}
          onEditTask={onEditTask}
          isAdmin={true}
        />
        <Text my={25}>Temps total effectué : {formatHoursAndMinutes(totalCumulatedTime)}</Text>
      </Card>
    </>
  );
}
